import { Bot, Headset, Timer, Users } from "lucide-react";
import { StatCard } from "@/components/ui/stat-card";
import { ChartCard } from "@/components/ui/chart-card";
import { LoadingState } from "@/components/ui/loading-state";
import { KpiPeriod, LatencyStats } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import { useOrgHandling } from "../hooks/useOrgHandling";
import { formatDuration, formatPercent } from "../org-kpis.meta";

interface HandlingTabProps {
  organizationId: string;
  period: KpiPeriod;
}

export function HandlingTab({ organizationId, period }: HandlingTabProps) {
  const { data, isLoading, error, refetch } = useOrgHandling(organizationId, period);

  if (isLoading || error || !data) {
    return (
      <LoadingState
        isLoading={isLoading}
        error={error}
        loadingText="Loading handling figures..."
        errorTitle="Failed to load handling figures"
        onRetry={() => refetch()}
      />
    );
  }

  const { automation, responsiveness, summary } = data;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Handled by the bot alone"
          value={formatPercent(automation.containmentRate)}
          hint={`${formatters.formatNumber(automation.contained)} of ${formatters.formatNumber(automation.conversations)} conversations`}
          icon={Bot}
          tone="primary"
        />
        <StatCard
          label="Needed a person"
          value={formatPercent(automation.escalationRate)}
          hint={`${formatters.formatNumber(automation.escalated)} conversations`}
          icon={Headset}
        />
        <StatCard
          label="Handovers taken"
          value={formatters.formatNumber(summary.handled)}
          hint={`${formatters.formatNumber(summary.transferred)} transferred, ${formatters.formatNumber(summary.openNow)} open now`}
        />
        <StatCard
          label="Agents online now"
          value={`${summary.activeAgents} / ${summary.totalAgents}`}
          hint="Live, out of every agent in the organization"
          icon={Users}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard
          title="First response time"
          description="From a customer's message to the first answer"
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <Latency label="Anyone" stats={responsiveness.anyResponse} />
            <Latency label="Bot" stats={responsiveness.botResponse} />
            <Latency label="Person" stats={responsiveness.humanResponse} />
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            {formatPercent(responsiveness.replyRate)} of customer messages got an answer;{" "}
            {formatPercent(responsiveness.humanReplyRate)} of those waiting for a person got one.
          </p>
        </ChartCard>
        <ChartCard title="Waiting right now" description="Live, whatever period is selected">
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between">
              <span>Queued, nobody picked up</span>
              <span className="tabular-nums">{automation.needsAttention.awaitingPickup}</span>
            </li>
            <li className="flex justify-between">
              <span>Nobody answered yet</span>
              <span className="tabular-nums">{automation.needsAttention.unanswered}</span>
            </li>
            <li className="flex justify-between">
              <span>Waiting on the agent</span>
              <span className="tabular-nums">{automation.needsAttention.waitingOnAgent}</span>
            </li>
          </ul>
        </ChartCard>
      </div>
    </div>
  );
}

function Latency({ label, stats }: { label: string; stats: LatencyStats }) {
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Timer className="h-3 w-3" />
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{formatDuration(stats.p50Ms)}</div>
      <div className="text-xs text-muted-foreground">
        median · 90% within {formatDuration(stats.p90Ms)}
      </div>
    </div>
  );
}
